import { useState, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { IHolderPayload, IGuestPayload, ICardPayload } from '@share/common-types';
import { validatePaymentField } from './payment-validation';
import { IActivityAgeBand, IAgeRange, QuestionGroupTraveler, getActivityAgeBandBounds, getGuestTypeForAge, isTravelerAgeBandQuestion } from '@common-types';
import { getVisibleBookingQuestionsForDetail, isTransferModeAnswerApplicable } from '@share/utils';

/** Integer age at end of calendar `activityDate` for a birth date (YYYY-MM-DD). */
export function computeAgeAtActivityDate(birthDateIso: string, activityDateIso: string): number {
    const birth = dayjs(birthDateIso, 'YYYY-MM-DD', true);
    const activity = dayjs(activityDateIso, 'YYYY-MM-DD', true);
    if (!birth.isValid() || !activity.isValid()) {
        return NaN;
    }
    return activity.diff(birth.startOf('day'), 'year');
}

/**
 * Resolves the AGE_BAND answer code from the guest’s age (from the activity detail / room selection) and question options.
 * Prefers `guest.type` when it is a valid option; otherwise picks a band by age brackets.
 */
export function getAgeBandAnswerCodeForQuestion(
    q: { options?: { code: string }[] },
    guest: IGuestPayload,
    ageBands?: IActivityAgeBand[] | null
): string | undefined {
    const rawAge = (guest as { age?: number | string | null }).age;
    if (rawAge === undefined || rawAge === null || rawAge === '') {
        return undefined;
    }
    const ageNum = Number(rawAge);
    if (!Number.isFinite(ageNum)) {
        return undefined;
    }
    const opts = q.options || [];
    const codes = new Set(opts.map((o) => o.code));
    if (guest?.type && codes.has(guest.type)) {
        return guest.type;
    }
    if (ageBands?.length) {
        for (const opt of opts) {
            const bounds = getActivityAgeBandBounds(opt.code, ageBands);
            if (bounds && ageNum >= bounds.startAge && ageNum <= bounds.endAge) {
                return opt.code;
            }
        }
    }
    const pick = (code: string) => (codes.has(code) ? code : undefined);
    if (ageNum >= 0 && ageNum <= 1 && pick('INFANT')) return 'INFANT';
    if (ageNum >= 12 && ageNum <= 24 && pick('YOUTH')) return 'YOUTH';
    if (ageNum >= 2 && ageNum <= 17 && pick('CHILD')) return 'CHILD';
    if (ageNum >= 18 && ageNum <= 59 && pick('ADULT')) return 'ADULT';
    if (ageNum >= 0 && ageNum <= 120 && pick('TRAVELER')) return 'TRAVELER';

    if (ageNum >= 60 && pick('SENIOR')) return 'SENIOR';
    if (ageNum >= 18 && pick('ADULT')) return 'ADULT';
    if (ageNum >= 2 && pick('CHILD')) return 'CHILD';
    if (ageNum <= 1 && pick('INFANT')) return 'INFANT';
    if (ageNum >= 0 && pick('TRAVELER')) return 'TRAVELER';
    return opts[0]?.code;
}

export function getGuestTypeAgeBandError(
    type: string | null,
    age: any,
    ageBands?: IActivityAgeBand[] | null
): string | undefined {
    if (age === undefined || age === null || age === '') {
        return undefined;
    }
    const ageNum = Number(age);
    if (type) {
        const bounds = getActivityAgeBandBounds(type, ageBands);
        if (bounds) {
            if (ageNum < bounds.startAge || ageNum > bounds.endAge) {
                return `Age must be ${bounds.startAge}–${bounds.endAge}`;
            }
            return undefined;
        }
    }
    if (type === 'INFANT') {
        if (ageNum < 0 || ageNum > 1) {
            return 'Infant age must be 0–1';
        }
    } else if (type === 'CHILD') {
        if (ageNum < 2 || ageNum > 17) {
            return 'Child age must be 2–17';
        }
    } else if (type === 'YOUTH') {
        if (ageNum < 12 || ageNum > 24) {
            return 'Youth age must be 12–24';
        }
    } else if (type === 'ADULT') {
        if (ageNum < 18 || ageNum > 59) {
            return 'Adult age must be 18–59';
        }
    } else if (type === 'SENIOR') {
        if (ageNum < 60 || ageNum > 99) {
            return 'Senior age must be 60+';
        }
    } else if (type === 'TRAVELER') {
        if (ageNum < 0 || ageNum > 120) {
            return 'Traveler age must be 0-120';
        }
    } else if (ageNum < 0 || ageNum > 99) {
        return 'Age must be 0–99';
    }
    return undefined;
}

export function getActivityAgeRangeError(ageRange: IAgeRange | undefined | null, age: number): string | undefined {
    if (!ageRange || !Number.isFinite(age)) {
        return undefined;
    }
    if (age < ageRange.minAge) {
        return `Minimum age for this activity is ${ageRange.minAge}`;
    }
    if (ageRange.maxAge != null && age > ageRange.maxAge) {
        return `Maximum age for this activity is ${ageRange.maxAge}`;
    }
    return undefined;
}

export function isGuestBirthDateCalendarDisabled(
    current: Dayjs,
    guest: IGuestPayload,
    activityDateIso: string,
    hasAgeBandQuestion: boolean,
    ageRange?: IAgeRange | null,
    ageBands?: IActivityAgeBand[] | null
): boolean {
    const activity = dayjs(activityDateIso, 'YYYY-MM-DD', true);
    if (!activity.isValid()) {
        return false;
    }
    if (current.isAfter(activity, 'day')) {
        return true;
    }
    const age = activity.diff(current.startOf('day'), 'year');
    if (!Number.isFinite(age) || age < 0 || age > 150) {
        return true;
    }
    if (ageRange) {
        if (age < ageRange.minAge) {
            return true;
        }
        if (ageRange.maxAge != null && age > ageRange.maxAge) {
            return true;
        }
    }
    return Boolean(getGuestTypeAgeBandError(hasAgeBandQuestion ? guest.type : null, age, ageBands));
}

/** Upper bound on age-at-activity for this guest (used to jump the calendar to the earliest plausible birth year). */
export function getMaxAllowedAgeForBirthPicker(
    guest: IGuestPayload,
    hasAgeBandQuestion: boolean,
    ageRange?: IAgeRange | null,
    ageBands?: IActivityAgeBand[] | null
): number {
    if (ageRange?.maxAge != null) {
        return ageRange.maxAge;
    }
    if (!hasAgeBandQuestion) {
        return 99;
    }
    const fromBands = getActivityAgeBandBounds(guest?.type, ageBands);
    if (fromBands) {
        return fromBands.endAge;
    }
    switch (guest.type) {
        case 'INFANT':
            return 1;
        case 'CHILD':
            return 17;
        case 'YOUTH':
            return 24;
        case 'ADULT':
            return 59;
        case 'SENIOR':
            return 99;
        case 'TRAVELER':
            return 120;
        default:
            return 99;
    }
}

/** Calendar opens on 1 Jan of the earliest birth year still allowed (activity date minus max age). */
export function getBirthDateDefaultPickerValue(
    activityDateIso: string,
    guest: IGuestPayload,
    hasAgeBandQuestion: boolean,
    ageRange?: IAgeRange | null,
    ageBands?: IActivityAgeBand[] | null
): Dayjs {
    const activity = dayjs(activityDateIso, 'YYYY-MM-DD', true);
    if (!activity.isValid()) {
        return dayjs().subtract(99, 'year').startOf('year');
    }
    const maxAge = getMaxAllowedAgeForBirthPicker(guest, hasAgeBandQuestion, ageRange, ageBands);
    return activity.subtract(maxAge, 'year').startOf('year');
}

export const checkoutValidationSchema = {
    guestInfo: {
        firstName: (value: string) => {
            if (!value) return "Please enter first name";
            return undefined;
        },
        lastName: (value: string) => {
            if (!value) return "Please enter last name";
            return undefined;
        },
        email: (value: string) => {
            if (!value) return "Please enter email address";
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!re.test(value)) return "Please enter a valid email address";
            return undefined;
        },
        phone: (value: string) => {
            if (!value) return "Please enter mobile phone number";
            return undefined;
        },
        age: (value: any) => {
            if (value === undefined || value === null || value === '') return "Please enter age";
            const num = Number(value);
            if (isNaN(num) || num < 0 || !Number.isInteger(num)) return "Please enter a valid age";
            return undefined;
        },
        birthDate: (value: any) => {
            if (value === undefined || value === null || value === '') {
                return 'Please select date of birth';
            }
            return undefined;
        },
        /*
        guestType: (value: any) => {
            if (value === undefined || value === null || value === '') return "Please select guest type";
            return undefined;
        }
        */
    }
};

export const validateCheckoutField = (section: 'guestInfo', field: string, value: any) => {
    const sectionSchema = (checkoutValidationSchema as any)[section];
    if (sectionSchema && sectionSchema[field]) {
        return sectionSchema[field](value);
    }
    return undefined;
};

export const validateField = (section: 'paymentMethod' | 'billingAddress' | 'guestInfo', field: string, value: any) => {
    if (section === 'guestInfo') {
        return validateCheckoutField(section, field, value);
    }
    return validatePaymentField(section as 'paymentMethod' | 'billingAddress', field, value);
};

export const useCheckoutValidation = (
    holder: IHolderPayload,
    setHolder: React.Dispatch<React.SetStateAction<IHolderPayload>>,
    paxes: IGuestPayload[],
    setPaxes: React.Dispatch<React.SetStateAction<IGuestPayload[]>>,
    card: ICardPayload,
    termsAccepted: boolean,
    cancellationAccepted: boolean,
    detailLabel?: any,
    bookingAnswers?: any,
    travelerAnswers?: any[],
    selectedLanguageGuide?: any,
    conditionalData?: any,
    questionData?: any,
    activityStartDate?: string
) => {
    const [errors, setErrors] = useState<any>({
        holder: {},
        paxes: [],
        card: {}
    });

    const validateHolder = () => {
        const holderErrors: any = {};
        const fields = ['givenName', 'surName', 'email', 'phone', 'addressLine', 'city', 'stateCode', 'postalCode', 'country'];

        fields.forEach(field => {
            if (field === 'stateCode' && holder.country !== 'US') return;

            let validateKey = field;
            let section: 'guestInfo' | 'billingAddress' = 'guestInfo';

            if (field === 'givenName') validateKey = 'firstName';
            if (field === 'surName') validateKey = 'lastName';
            if (['addressLine', 'city', 'stateCode', 'postalCode', 'country'].includes(field)) {
                section = 'billingAddress';
                if (field === 'addressLine') validateKey = 'address';
                if (field === 'stateCode') validateKey = 'state';
                if (field === 'postalCode') validateKey = 'postalCode';
            }

            const error = validateField(section, validateKey, (holder as any)[field]);
            if (error) holderErrors[field] = error;
        });

        setErrors((prev: any) => ({ ...prev, holder: holderErrors }));
        return Object.keys(holderErrors).length === 0;
    };

    const validatePaxes = () => {
        const paxesErrors = paxes.map(guest => {
            const guestErrors: any = {};

            const givenNameError = validateField('guestInfo', 'firstName', guest.givenName || '');
            if (givenNameError) guestErrors.givenName = givenNameError;

            const surNameError = validateField('guestInfo', 'lastName', guest.surName || '');
            if (surNameError) guestErrors.surName = surNameError;

            const ageError = validateField('guestInfo', 'age', guest.age);
            if (ageError) {
                guestErrors.age = ageError;
            } else {
                const hasAgeBandQuestion = detailLabel?.questions?.some(
                    (q: any) => isTravelerAgeBandQuestion(q) && QuestionGroupTraveler.includes(q.group)
                );
                const bandError = getGuestTypeAgeBandError(hasAgeBandQuestion ? guest.type : null, guest.age, detailLabel?.ageBands);
                const activityRangeError = getActivityAgeRangeError(detailLabel?.ageRange, Number(guest.age));
                const rangeError = bandError || activityRangeError;
                if (rangeError) guestErrors.age = rangeError;
            }

            const typeError = validateField('guestInfo', 'guestType', guest.type);
            if (typeError) guestErrors.type = typeError;
            
            return guestErrors;
        });

        setErrors((prev: any) => ({ ...prev, paxes: paxesErrors }));
        return paxesErrors.every(e => Object.keys(e).length === 0);
    };

    const handleHolderChange = (field: string, value: any) => {
        let val = value;
        if (field === 'phone') {
            // Remove non-numeric characters and limit to 15 digits
            val = value.replace(/\D/g, '').slice(0, 15);
        }

        let newHolder = { ...holder, [field]: val };

        if (field === 'country' || field === 'countryCode') {
            const countryVal = value;
            newHolder.country = countryVal;
            newHolder.countryCode = countryVal;

            // Clear state if country is not US
            if (countryVal !== 'US') {
                newHolder.stateCode = '';
            }
        }

        setHolder(newHolder);

        let validateKey = field;
        let section: 'guestInfo' | 'billingAddress' = 'guestInfo';

        if (field === 'givenName') validateKey = 'firstName';
        if (field === 'surName') validateKey = 'lastName';
        if (field === 'email') validateKey = 'email';
        if (field === 'phone') validateKey = 'phone';
        if (['addressLine', 'city', 'stateCode', 'postalCode', 'country'].includes(field)) {
            section = 'billingAddress';
            if (field === 'addressLine') validateKey = 'address';
            if (field === 'city') validateKey = 'city';
            if (field === 'stateCode') validateKey = 'state';
            if (field === 'postalCode') validateKey = 'postalCode';
            if (field === 'country') validateKey = 'country';
        }

        const error = validateField(section, validateKey, val);

        setErrors((prev: any) => {
            const newHolderErrors = { ...prev.holder, [field]: error };

            // If country changed and is not US, clear state error
            if ((field === 'country' || field === 'countryCode') && value !== 'US') {
                delete newHolderErrors.stateCode;
            }

            return {
                ...prev,
                holder: newHolderErrors
            };
        });
    };

    const handlePaxChange = (idx: number, field: string, value: any) => {
        const newPaxes = [...paxes];
        let val: any = value;

        if (field === 'age') {
            val = value === '' || value === null ? '' : parseInt(String(value), 10);
            (newPaxes[idx] as any)[field] = val;
            if (detailLabel?.ageBands?.length) {
                if (val === '' || val === null) {
                    /* keep existing type */
                } else {
                    (newPaxes[idx] as any).type = getGuestTypeForAge(
                        val,
                        detailLabel.ageBands,
                        paxes[idx]?.type || 'ADULT'
                    );
                }
            }
        } else if (field === 'birthDate') {
            const raw =
                typeof value === 'string'
                    ? value
                    : value && dayjs(value).isValid()
                      ? dayjs(value).format('YYYY-MM-DD')
                      : '';
            newPaxes[idx] = { ...newPaxes[idx], birthDate: raw || undefined } as IGuestPayload;
        } else {
            (newPaxes[idx] as any)[field] = val;
        }

        setPaxes(newPaxes);

        const currentGuest = newPaxes[idx];
        const newPaxesErrors = [...errors.paxes];
        const updatedIdxError = { ...newPaxesErrors[idx] };

        let validateKey = '';
        if (field === 'givenName') validateKey = 'firstName';
        if (field === 'surName') validateKey = 'lastName';
        if (field === 'age') validateKey = 'age';
        if (field === 'type') validateKey = 'guestType';

        if (validateKey) {
            if (field === 'givenName' || field === 'surName') {
                updatedIdxError[field] = validateField('guestInfo', validateKey, val);
            } else if (field === 'type') {
                updatedIdxError.type = validateField('guestInfo', 'guestType', val);
            }
        }

        const hasAgeBandQuestion = detailLabel?.questions?.some(
            (q: any) => isTravelerAgeBandQuestion(q) && QuestionGroupTraveler.includes(q.group)
        );

        if (field === 'age' || field === 'type') {
            const ageBase = validateField('guestInfo', 'age', currentGuest.age);
            updatedIdxError.age =
                ageBase ||
                getGuestTypeAgeBandError(hasAgeBandQuestion ? currentGuest.type : null, currentGuest.age, detailLabel?.ageBands) ||
                getActivityAgeRangeError(detailLabel?.ageRange, Number(currentGuest.age));
        }

        newPaxesErrors[idx] = updatedIdxError;
        setErrors((prev: any) => ({ ...prev, paxes: newPaxesErrors }));
    };

    const isStep1Valid = useMemo(() => {
        const fields = ['givenName', 'surName', 'email', 'phone', 'addressLine', 'city', 'stateCode', 'postalCode', 'country'];
        return fields.every(field => {
            if (field === 'stateCode' && holder.country !== 'US') return true;

            let validateKey = field;
            let section: 'guestInfo' | 'billingAddress' = 'guestInfo';
            if (field === 'givenName') validateKey = 'firstName';
            if (field === 'surName') validateKey = 'lastName';
            if (field === 'email') validateKey = 'email';
            if (field === 'phone') validateKey = 'phone';
            if (['addressLine', 'city', 'stateCode', 'postalCode', 'country'].includes(field)) {
                section = 'billingAddress';
                if (field === 'addressLine') validateKey = 'address';
                if (field === 'city') validateKey = 'city';
                if (field === 'stateCode') validateKey = 'state';
                if (field === 'postalCode') validateKey = 'postalCode';
                if (field === 'country') validateKey = 'country';
            }
            const valid = !validateField(section, validateKey, (holder as any)[field]);

            if (!valid) {
                console.error('section', section);
                console.error('validateKey', validateKey);
                console.error('holder', holder);
                console.error('field', field);
                console.error('error', validateField(section, validateKey, (holder as any)[field]));
                console.error('--------------------------------');
            }

            return valid;
        });
    }, [holder]);

    const isStep2Valid = useMemo(() => {
        if (paxes.length === 0) return false;

        // 1. Basic Pax Validation
        const hasAgeBandQuestion = detailLabel?.questions?.some(
            (q: any) => isTravelerAgeBandQuestion(q) && QuestionGroupTraveler.includes(q.group)
        );

        const isBasicPaxValid = paxes.every(guest => {
            const valid = (
                !validateField('guestInfo', 'firstName', guest.givenName) &&
                !validateField('guestInfo', 'lastName', guest.surName) &&
                !validateField('guestInfo', 'age', guest.age) &&
                !getGuestTypeAgeBandError(hasAgeBandQuestion ? guest.type : null, guest.age, detailLabel?.ageBands) &&
                !getActivityAgeRangeError(detailLabel?.ageRange, Number(guest.age)) &&
                !validateField('guestInfo', 'guestType', guest.type)
            );

            if (!valid) {
                console.error('guest', guest);
                console.error('error givenName', validateField('guestInfo', 'firstName', guest.givenName));
                console.error('error surName', validateField('guestInfo', 'lastName', guest.surName));
                console.error('error age', validateField('guestInfo', 'age', guest.age));
                console.error('error ageBand', getGuestTypeAgeBandError(hasAgeBandQuestion ? guest.type : null, guest.age, detailLabel?.ageBands));
                console.error('error ageRange', getActivityAgeRangeError(detailLabel?.ageRange, Number(guest.age)));
                console.error('error guestType', validateField('guestInfo', 'guestType', guest.type));
                console.error('--------------------------------');
            }

            return valid;
        });

        if (!isBasicPaxValid) return false;

        // 2. Language Guide Validation
        if (detailLabel?.languageGuides && detailLabel.languageGuides.length > 0) {
            if (!selectedLanguageGuide) return false;
        }

        // 3. Traveler Questions Validation
        if (travelerAnswers && detailLabel?.questions) {
            const hasInvalidTravelerAnswer = travelerAnswers.some((traveler) => {
                return detailLabel.questions.some((q: any) => {
                    const isGuestGroup = QuestionGroupTraveler.includes(q.group);
                    const label = q.text?.toLowerCase() || '';
                    const isDuplicate =
                        label.includes('first name') ||
                        label.includes('last name') ||
                        label === 'age' ||
                        isTravelerAgeBandQuestion(q);
                    const isRequired = q.required === 'true' || q.required === true;

                    if (isRequired && isGuestGroup && !isDuplicate) {
                        const answer = traveler[q.code];
                        if (!answer?.value || (typeof answer.value === 'string' && answer.value.trim() === '')) {
                            console.error('question code', q.code);
                            console.error('traveler', traveler);
                            console.error('error', answer?.value);
                            console.error('--------------------------------');
                            return true; // Invalid
                        }
                    }
                    return false;
                });
            });

            if (hasInvalidTravelerAnswer) return false;
        }

        // 4. Booking Questions Validation (including conditional) — same visible set as checkout (rate/session + conditionals)
        if (bookingAnswers && detailLabel?.questions) {
            const visible = getVisibleBookingQuestionsForDetail(
                detailLabel.questions,
                bookingAnswers,
                conditionalData
            );
            for (const q of visible) {
                const qId = q.code || (q as { id?: string }).id;
                if (qId === undefined || qId === '') {
                    continue;
                }
                const requiredVal = (q as { required?: boolean | string }).required;
                const isRequired = requiredVal === true || requiredVal === 'true';
                const answer = bookingAnswers[String(qId)];
                const rawVal = answer?.value;
                const hasVal =
                    rawVal !== undefined &&
                    rawVal !== null &&
                    (typeof rawVal !== 'string' || String(rawVal).trim() !== '');

                if (
                    (qId === 'TRANSFER_ARRIVAL_MODE' || qId === 'TRANSFER_DEPARTURE_MODE') &&
                    hasVal &&
                    !isTransferModeAnswerApplicable(
                        String(qId),
                        rawVal,
                        detailLabel.questions,
                        questionData?.questions
                    )
                ) {
                    console.error('question code', qId);
                    console.error('bookingAnswers', bookingAnswers);
                    console.error('error', answer?.value);
                    console.error('--------------------------------');
                    return false;
                }

                if (isRequired) {
                    if (!answer?.value || (typeof answer.value === 'string' && answer.value.trim() === '')) {
                        console.error('question code', qId);
                        console.error('bookingAnswers', bookingAnswers);
                        console.error('error', answer?.value);
                        console.error('--------------------------------');
                        return false;
                    }
                }
            }
        }

        return true;
    }, [paxes, detailLabel, bookingAnswers, travelerAnswers, selectedLanguageGuide, conditionalData, questionData, activityStartDate]);

    const isStep3Valid = useMemo(() => {
        const formData = {
            nameOnCard: card.holderName,
            cardNumber: card.cardNumber,
            expiryMonth: card.expireDate.substring(0, 2),
            expiryYear: card.expireDate.substring(2),
            securityCode: card.cvv
        };
        const isCardValid = !validateField('paymentMethod', 'nameOnCard', formData.nameOnCard) &&
            !validateField('paymentMethod', 'cardNumber', formData.cardNumber) &&
            !validateField('paymentMethod', 'expiryMonth', formData.expiryMonth) &&
            !validateField('paymentMethod', 'expiryYear', formData.expiryYear) &&
            !validateField('paymentMethod', 'securityCode', formData.securityCode);

        return isCardValid && termsAccepted && cancellationAccepted;
    }, [card, termsAccepted, cancellationAccepted]);

    return {
        errors,
        setErrors,
        validateHolder,
        validatePaxes,
        handleHolderChange,
        handlePaxChange,
        isStep1Valid,
        isStep2Valid,
        isStep3Valid
    };
};
